import { propertyTypes } from "../../@types/sharedSchema";

// ──────────────────────────────────────────────────────────────────────────────
// Logical grouping for the "סוג נכס" dropdown.
//
// The options that reach the UI are the RAW HEBREW strings of the shared
// `propertyTypes` enum (Algolia indexes `propertyType` as the Hebrew label and
// `usePropertyTypes` returns facet values verbatim), so the grouping keys off
// those strings.
//
// Invariant: every option lands in exactly one group. Anything not listed below
// — a newly added enum member, or a stray facet value from an older document —
// falls through to OTHER rather than vanishing from the dropdown. That
// catch-all is the reason this is a lookup with a default and not a filter.
// ──────────────────────────────────────────────────────────────────────────────

export const PROPERTY_TYPE_GROUP_IDS = ["apartments", "houses", "commercial", "other"] as const;

export type PropertyTypeGroupId = typeof PROPERTY_TYPE_GROUP_IDS[number];

export const PROPERTY_TYPE_GROUP_LABELS: Record<PropertyTypeGroupId, string> = {
  apartments: "דירות",
  houses: "צמודי קרקע",
  commercial: "מסחרי",
  other: "אחר"
};

const OTHER: PropertyTypeGroupId = "other";

/**
 * Explicit membership. Strings must match `propertyTypes` in sharedSchema.ts
 * byte for byte — note the space after the slash in "בית פרטי/ קוטג'" and
 * friends. `propertyTypeGroupCoverage()` below is what keeps this honest.
 */
const GROUP_MEMBERS: Record<PropertyTypeGroupId, readonly string[]> = {
  apartments: [
    "דירה",
    "דירת גן",
    "גג/ פנטהאוז",
    "דופלקס",
    "טריפלקס",
    "סטודיו",
    "סטודיו/ לופט",
    "יחידת דיור",
    "דירה מחולקת",
    "מרתף/ פרטר"
  ],
  houses: ["בית פרטי/ קוטג'", "דו משפחתי", "משק חקלאי/ נחלה", "משק עזר", "מגרשים"],
  commercial: [
    "משרדים",
    "בניין משרדים",
    "חנויות/ שטח מסחרי",
    "מחסן",
    "מחסנים",
    "מבני תעשייה",
    "חלל עבודה משותף",
    "מרפאה",
    "מסעדה",
    "אולמות",
    "בית מלון",
    "תיירות ונופש"
  ],
  other: ["חניה", "דיור מוגן", "בניין מגורים", "קב' רכישה/ זכות לנכס", "נכס מניב", "כללי"]
};

const GROUP_BY_TYPE: ReadonlyMap<string, PropertyTypeGroupId> = new Map(
  PROPERTY_TYPE_GROUP_IDS.flatMap((groupId) =>
    GROUP_MEMBERS[groupId].map((type) => [type, groupId] as const)
  )
);

/** Group a single option. Unknown options are bucketed into `other`. */
export function getPropertyTypeGroup(propertyType: string): PropertyTypeGroupId {
  return GROUP_BY_TYPE.get(propertyType) ?? OTHER;
}

export type PropertyTypeGroup = {
  id: PropertyTypeGroupId;
  label: string;
  options: string[];
};

/**
 * Bucket an arbitrary option list (typically the Algolia facet values, which
 * only contain types actually present in the index) into ordered, non-empty
 * groups. Order within a group follows the caller's list, so an Algolia
 * count-descending facet keeps its "most common first" ordering inside each
 * section.
 */
export function groupPropertyTypes(options: readonly string[]): PropertyTypeGroup[] {
  const buckets = new Map<PropertyTypeGroupId, string[]>();

  options.forEach((option) => {
    const groupId = getPropertyTypeGroup(option);
    const bucket = buckets.get(groupId);
    if (bucket) bucket.push(option);
    else buckets.set(groupId, [option]);
  });

  return PROPERTY_TYPE_GROUP_IDS.filter((id) => buckets.get(id)?.length).map((id) => ({
    id,
    label: PROPERTY_TYPE_GROUP_LABELS[id],
    options: buckets.get(id) as string[]
  }));
}

/**
 * Guard used by the unit test: reports any shared-enum member that was never
 * assigned a group (so it would silently land in "אחר"), and any string listed
 * here that no longer exists in the enum (a typo, or a renamed value).
 */
export function propertyTypeGroupCoverage(): {
  unassigned: string[];
  unknown: string[];
} {
  const listed = new Set(GROUP_BY_TYPE.keys());
  const known = new Set<string>(propertyTypes);

  return {
    unassigned: propertyTypes.filter((type) => !listed.has(type)),
    unknown: Array.from(listed).filter((type) => !known.has(type))
  };
}
